import { useState } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "../lib/trpc";
import { useTranslation } from "../i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import { User, Mail, Lock, Save, LogOut } from "lucide-react";
import { useDateFormat } from '@/contexts/DateFormatContext';
import DashboardLayout from "../components/DashboardLayout";

export default function Profile() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { formatDate, formatDateTime } = useDateFormat();
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const utils = trpc.useUtils();

  const updateProfile = trpc.auth.updateProfile.useMutation({
    onSuccess: () => {
      toast.success(t.profile.profileUpdated);
      utils.auth.me.invalidate();
    },
    onError: (error) => {
      toast.error(error.message || t.profile.errors.profileUpdateFailed);
    },
  });

  const changePassword = trpc.auth.changePassword.useMutation({
    onSuccess: () => {
      toast.success(t.profile.passwordChanged);
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
    },
    onError: (error) => {
      toast.error(error.message || t.profile.errors.passwordChangeFailed);
    },
  });

  const handleProfileUpdate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !email) {
      toast.error(t.profile.errors.nameEmailRequired);
      return;
    }
    updateProfile.mutate({ name, email });
  };

  const handlePasswordChange = (e: React.FormEvent) => {
    e.preventDefault();

    if (!oldPassword || !newPassword || !confirmPassword) {
      toast.error(t.profile.errors.allFieldsRequired);
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error(t.profile.errors.passwordMismatch);
      return;
    }

    if (newPassword.length < 8) {
      toast.error(t.profile.errors.passwordTooShort);
      return;
    }

    changePassword.mutate({ oldPassword, newPassword });
  };

  return (
    <DashboardLayout>
      <div className="container max-w-4xl py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <User className="h-7 w-7 text-purple-600" />
            {t.profile.title}
          </h1>
          <p className="text-muted-foreground mt-2">
            {t.profile.subtitle}
          </p>
        </div>

        <div className="space-y-6">
          {/* Profile Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                {t.profile.profileInfo}
              </CardTitle>
              <CardDescription>
                {t.profile.profileInfoDesc}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleProfileUpdate} className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="name" className="text-sm font-medium">
                    {t.profile.name}
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="name"
                      type="text"
                      placeholder={t.profile.name}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label htmlFor="email" className="text-sm font-medium">
                    {t.profile.email}
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder={t.profile.email}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2 pt-2">
                  <Button
                    type="submit"
                    disabled={updateProfile.isPending}
                  >
                    <Save className="mr-2 h-4 w-4" />
                    {updateProfile.isPending ? t.common.loading : t.profile.updateProfile}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Password Change */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5" />
                {t.profile.security}
              </CardTitle>
              <CardDescription>
                {t.profile.securityDesc}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="oldPassword" className="text-sm font-medium">
                    {t.profile.oldPassword}
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="oldPassword"
                      type="password"
                      placeholder={t.profile.oldPassword}
                      value={oldPassword}
                      onChange={(e) => setOldPassword(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                <Separator />

                <div className="space-y-2">
                  <label htmlFor="newPassword" className="text-sm font-medium">
                    {t.profile.newPasswordLabel}
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="newPassword"
                      type="password"
                      placeholder={t.profile.newPasswordLabel}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="pl-10"
                      minLength={8}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label htmlFor="confirmPassword" className="text-sm font-medium">
                    {t.profile.confirmPasswordLabel}
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder={t.profile.confirmPasswordLabel}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-10"
                      minLength={8}
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2 pt-2">
                  <Button
                    type="submit"
                    disabled={changePassword.isPending}
                  >
                    <Lock className="mr-2 h-4 w-4" />
                    {changePassword.isPending ? t.common.loading : t.profile.updatePassword}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Account Information */}
          <Card>
            <CardHeader>
              <CardTitle>{t.profile.accountInfo}</CardTitle>
              <CardDescription>
                {t.profile.accountDetails}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t.profile.role}:</span>
                <span className="font-medium">{user?.role === "admin" ? t.profile.roleAdmin : t.profile.roleUser}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t.profile.createdAt}:</span>
                <span className="font-medium">
                  {user?.createdAt ? formatDate(user.createdAt) : "-"}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t.profile.lastLogin}:</span>
                <span className="font-medium">
                  {user?.lastSignedIn ? formatDateTime(user.lastSignedIn) : "-"}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Logout Section */}
          <Card className="border-destructive/50">
            <CardHeader>
              <CardTitle className="text-destructive">{t.profile.logoutSection}</CardTitle>
              <CardDescription>
                {t.profile.logoutDesc}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                variant="destructive"
                onClick={logout}
                className="w-full"
              >
                <LogOut className="mr-2 h-4 w-4" />
                {t.profile.logoutButton}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
